import { useState } from "react";
import { useQuery } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import { Table, Grid, Title, Text, Button, Modal } from "@mantine/core";

import { AddressDisplay, GroupType, STYLE } from "@jasper/shared";

import { GET_GROUP_BY_ID } from "../../gql/groups";
import AddNewMemberToGroupModal from "./modals/AddNewMemberToGroupModal";

import { useUser } from "../../hooks/useUser";

const GroupView = () => {
  const [isNewMemberModalOpen, setIsNewMemberModalOpen] =
    useState<boolean>(false);

  const { group_id } = useParams();
  const navigate = useNavigate();

  const { updateUser } = useUser();

  const { data, refetch } = useQuery(GET_GROUP_BY_ID, {
    variables: {
      where: {
        id: group_id,
      },
    },
    fetchPolicy: "network-only",
  });

  const deleteMemberFromGroup = async (user_id: string) => {
    const userUpdated = await updateUser({
      variables: {
        where: {
          id: user_id,
        },
        data: {
          userGroup: {
            disconnect: {
              id: {
                equals: group_id,
              },
            },
          },
        },
      },
    });
    if (userUpdated) {
      refetch();
    }
  };

  const displayCreatedBy = (
    createdBy: { firstName: string; lastName: string } | null
  ) => {
    if (!createdBy) {
      return "Unknown";
    }
    return `${createdBy.firstName} ${createdBy.lastName}`;
  };

  const PRICING_DISPLAY = {
    FULL: "10€",
    HALF: "5€",
    FREE: "0€ (Free)",
  } as const;

  return (
    <>
      <Modal
        opened={isNewMemberModalOpen}
        onClose={() => setIsNewMemberModalOpen(false)}
        title="Add a new member to this group"
      >
        <AddNewMemberToGroupModal
          group_id={group_id}
          closeModal={() => setIsNewMemberModalOpen(false)}
          refetch={refetch}
        />
      </Modal>
      <Grid style={{ textAlign: "center", paddingBottom: "2rem" }}>
        <Grid.Col span={4}>
          <Title fw={200}>Group name</Title>
          <Text>{data?.getUserGroupsWhereUnique?.name}</Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Title fw={200}>Group type</Title>
          <Text>{data?.getUserGroupsWhereUnique?.groupType}</Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Title fw={200}>Clinic Number</Title>
          <Text>{data?.getUserGroupsWhereUnique?.clinicNumber}</Text>
        </Grid.Col>
        {data?.getUserGroupsWhereUnique?.groupType === GroupType.DENTIST && (
          <>
            <Grid.Col span={4}>
              <Title fw={200}>Pickup carrier</Title>
              <Text>{data?.getUserGroupsWhereUnique?.pickupCarrier}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Title fw={200}>Delivery carrier</Title>
              <Text>{data?.getUserGroupsWhereUnique?.deliveryCarrier}</Text>
            </Grid.Col>
            {(data?.getUserGroupsWhereUnique?.addresses ?? []).length > 0 && (
              <Grid.Col span={4}>
                <Title fw={200}>Address</Title>
                <AddressDisplay
                  address={data?.getUserGroupsWhereUnique?.addresses[0]}
                />
              </Grid.Col>
            )}
            <Grid.Col span={4}>
              <Title fw={200}>Phone number</Title>
              <Text>{data?.getUserGroupsWhereUnique?.phoneNumber}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Title fw={200}>Pricing Type</Title>
              <Text>
                {data?.getUserGroupsWhereUnique?.clientBilling?.pricingType ||
                  "N/A"}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Title fw={200}>3D Model Pricing</Title>
              <Text>
                {data?.getUserGroupsWhereUnique?.threeDModelPricingType ||
                  "FULL"}
                {" - "}
                {PRICING_DISPLAY[
                  data?.getUserGroupsWhereUnique
                    ?.threeDModelPricingType as keyof typeof PRICING_DISPLAY
                ] || PRICING_DISPLAY.FULL}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Title fw={200}>Original Abutment Surcharge</Title>
              <Text>
                {data?.getUserGroupsWhereUnique?.exemptOriginalAbutmentCharge
                  ? "Free"
                  : "Charged"}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Title fw={200}>Show Dentist Traceability Prices</Title>
              <Text>
                {data?.getUserGroupsWhereUnique
                  ?.hidePricesOnPractitionerTraceability
                  ? "False"
                  : "True"}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Title fw={200}>Created By</Title>
              <Text>
                {displayCreatedBy(data?.getUserGroupsWhereUnique?.createdBy)}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Title fw={200}>Status</Title>
              <Text>{data?.getUserGroupsWhereUnique?.status}</Text>
            </Grid.Col>
          </>
        )}
      </Grid>
      <div style={{ textAlign: "center" }}>
        <Button
          style={{ backgroundColor: STYLE.primary }}
          onClick={() => navigate(`/groups/${group_id}/edit`)}
        >
          Update group
        </Button>
      </div>
      <Title fw={200} style={{ paddingBottom: "1rem" }}>
        Members:
      </Title>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Lastname</Table.Th>
            <Table.Th>Firstname</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Is master account</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(data?.getUserGroupsWhereUnique?.groupMembers ?? []).map(member => {
            return (
              <Table.Tr key={member.id}>
                <Table.Td>{member.lastName}</Table.Td>
                <Table.Td>{member.firstName}</Table.Td>
                <Table.Td
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                  onClick={() => navigate(`/users/${member.id}`)}
                >
                  {member.email}
                </Table.Td>
                <Table.Td>{member.isManager ? "YES" : "NO"}</Table.Td>
                <Table.Td
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                  onClick={() => deleteMemberFromGroup(member.id)}
                >
                  Delete
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          marginTop: "3rem",
        }}
      >
        <Button
          style={{ backgroundColor: STYLE.primary }}
          onClick={() => setIsNewMemberModalOpen(true)}
        >
          Add an existing user as a member
        </Button>
        <Button
          style={{ backgroundColor: STYLE.primary }}
          onClick={() =>
            navigate(`/users/new/`, {
              state: {
                user: {
                  userGroup: {
                    id: group_id,
                    clinicNumber: data?.getUserGroupsWhereUnique?.clinicNumber,
                  },
                },
              },
            })
          }
        >
          Create a new user and add it as a member
        </Button>
      </div>
      {data?.getUserGroupsWhereUnique?.description && (
        <>
          <Title fw={200} style={{ paddingBottom: "1rem" }}>
            Description:
          </Title>
          <Text style={{ whiteSpace: "pre-wrap" }}>
            {data.getUserGroupsWhereUnique.description}
          </Text>
        </>
      )}
    </>
  );
};

export default GroupView;
